import React, { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { updateTask } from "../../store/tasks/slice";
import { apiErrorMessage } from "../../utils/apiError";
import DatePicker from "../common/DatePicker/DatePicker";
import Icons from "../common/Icons";
import { WarningDialog } from "../common/Modals/WarningDialog";
import Select from "../common/Select";
import { TaskDelete } from "../TaskDelete/TaskDelete";

const TagList = [
  "Research",
  "Synthesis",
  "Ideation",
  "Prototyping",
  "Testing",
  "Refinement",
  "Planning",
  "Execution",
  "Presentation",
  "Reflection",
];

/**
 * PRESENTATION ONLY. `TagList` entries are simultaneously the option `value`,
 * the option `label` and the persisted `tags` payload (brief 3.9, reproduced in
 * section 7.5), so renaming an entry would change what is stored. The array is
 * frozen, including `"Reflection"`, and only the rendered label moves.
 *
 * RED: change the value side and a submitted `tags` field stops being
 * byte-identical, which the focused test in this component's test file asserts
 * in the SAME test as the label, so a future edit cannot conflate the two.
 */
const TAG_LABEL = { Reflection: "Your Take" };

const formatDateToMMDDYYYY = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const TaskModifyForm = ({ isOpen, onClose, task }) => {
  const dispatch = useDispatch();
  const { milestones } = useSelector((state) => state.dashboard);
  const { squadOnlyUsers } = useSelector((state) => state.tasks);
  const [taskName, setTaskName] = useState(task?.title || "");
  const [error, setError] = useState({ taskName: "" });
  const [isCancelWarningOpen, setIsCancelWarningOpen] = useState(false);
  const [isDeleteTaskOpen, setIsDeleteTaskOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false); // State for DatePicker
  const [dueDate, setDueDate] = useState(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(new Date(task?.due_date)),
  );

  // A refusal belongs to ONE task and ONE opening of the drawer, so it is
  // cleared on both. `task.id` covers the page swapping in a different task
  // while the drawer stays rendered (both mounts guard on truthiness, not a
  // key). `isOpen` covers close-and-reopen: today both mounts also unmount on
  // close, so that path resets state anyway, but the reset must not depend on
  // a caller's teardown choice. Same deps as the sibling TaskDelete dialog,
  // which IS kept mounted and needs both.
  useEffect(() => {
    setError({ taskName: "" });
  }, [task?.id, isOpen]);

  if (!isOpen || !task) return null;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!taskName) {
      setError({ taskName: "Task name is required" });
      return;
    }

    const formData = new FormData(e.target);
    const taskData = Object.fromEntries(formData.entries());
    // Prepare quest data
    const updateTaskData = {
      id: task.id,
      title: taskName,
      description: taskData.description,
      due_date: dueDate,
      user_id: parseInt(taskData.assignedTo, 10),
      creator_user_id: parseInt(taskData.assignedBy, 10),
      tags: taskData.tag,
      status_id: parseInt(taskData.status, 10),
      squad_id: task.squad_id,
    };
    try {
      await dispatch(updateTask(updateTaskData)).unwrap();
      onClose();
    } catch (err) {
      // `updateTask` rejects with the raw response body. Laravel's VALIDATION
      // envelope carries per-field `errors`, and those are still rendered
      // field by field below. Every other refusal (a gate denial, a typed 409,
      // a 500) carries `message` and no `errors` at all, so reading `errors`
      // alone ran `setError(undefined)` and left the drawer looking exactly as
      // it did before SAVE CHANGES was pressed, with the write refused.
      const fieldErrors =
        err?.errors && typeof err.errors === "object" ? err.errors : {};
      setError({
        ...fieldErrors,
        submit: apiErrorMessage(
          err,
          "Your changes could not be saved. Try again.",
        ),
      });
    }
  };

  const handleDateSelect = (date) => {
    setDueDate(formatDateToMMDDYYYY(date));
    setShowDatePicker(false);
  };

  const openCancelWarning = () => setIsCancelWarningOpen(true);

  return (
    <>
      <div className="fixed inset-0 bg-darkblue bg-opacity-65 flex items-center justify-end">
        <div className="bg-background-blackpearl shadow-lg w-2/5 h-screen overflow-y-auto scrollbar-hidden relative">
          {/* Header */}
          <div className="sticky top-0 bg-background-blackpearl w-full flex justify-between items-center pl-6 pr-3 pt-6 pb-4 z-10">
            <div>
              <h2 className="text-sm font-normal text-[#B4C1E4] uppercase">
                NEW
              </h2>
              <div className="flex items-center p-1 gap-2 border-b border-[#F4F6FB]">
                <input
                  type="text"
                  name="taskName"
                  defaultValue={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="NEW TASK NAME"
                  className="bg-transparent text-white text-xl font-light outline-none placeholder:text-white w-[150px]"
                />
                <span className="inline-block">
                  <Icons type="pen" className="w-5 h-5" color="white" />
                </span>
              </div>
              {error?.taskName && (
                <p className="text-persimmon text-xs mt-1">{error.taskName}</p>
              )}
            </div>
            <button
              onClick={() => {
                openCancelWarning();
              }}
              className="text-white hover:text-gray-300"
            >
              <Icons type="close" className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form
            className="flex flex-col pt-6 px-6 gap-y-6"
            onSubmit={handleFormSubmit}
          >
            {/* Assigned By */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs">assigned By</label>
              <Select
                name="assignedBy"
                defaultValue={task.creator_user_id}
                options={squadOnlyUsers.map((user) => ({
                  value: user.id.toString(),
                  label: user.first_name + " " + user.last_name,
                }))}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            {/* Assigned To */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                assigned to
              </label>
              <Select
                name="assignedTo"
                defaultValue={task.user_id}
                options={squadOnlyUsers.map((user) => ({
                  value: user.id.toString(),
                  label: user.first_name + " " + user.last_name,
                }))}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            <p className="w-full border-b border-[#B4C1E4]"></p>

            {/* Due Date */}
            <div className="space-y-1 relative">
              <label className="text-[#F4F6FB] text-xs font-medium">
                due date
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="dueDate"
                  defaultValue={task.due_date?.split("T")[0]}
                  value={dueDate}
                  readOnly
                  required
                  onClick={() => setShowDatePicker(true)}
                  className="w-full bg-blackpearl border border-[#B4C1E4] rounded-md p-2 text-[#F4F6FB] pl-3 pr-10 cursor-pointer"
                />
                <Icons
                  type="calendar"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 cursor-pointer"
                  onClick={() => setShowDatePicker(true)}
                />
              </div>
              {showDatePicker && (
                <div className="absolute mt-2 z-50 w-full">
                  <DatePicker
                    onDateSelect={handleDateSelect}
                    onClose={() => setShowDatePicker(false)}
                  />
                </div>
              )}
              {error?.due_date && (
                <p className="text-persimmon text-xs mt-1">{error.due_date}</p>
              )}
            </div>

            {/* Tag */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">tag</label>
              <Select
                name="tag"
                defaultValue={task.tags}
                options={TagList.map((tag) => ({
                  value: tag,
                  label: TAG_LABEL[tag] ?? tag,
                }))}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                status
              </label>
              <Select
                name="status"
                defaultValue={task.status_id}
                options={[
                  { value: "1", label: "NEW" },
                  { value: "3", label: "In Progress" },
                  { value: "5", label: "Completed" },
                ]}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            <p className="w-full border-b border-[#B4C1E4]"></p>

            {/* Description */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                description
              </label>
              <textarea
                placeholder="Enter description of task"
                className="w-full bg-blackpearl border border-[#B4C1E4] rounded-md pl-4 py-2 text-[#F4F6FB] h-28 resize-none focus:outline-none"
                name="description"
                defaultValue={task.description}
                required
              />
              {error?.description && (
                <p className="text-persimmon text-xs mt-1">
                  {error.description}
                </p>
              )}
            </div>

            {error?.submit && (
              <p role="alert" className="text-persimmon text-xs">
                {error.submit}
              </p>
            )}

            {/* Buttons */}
            <div className="sticky bottom-0 flex justify-between pt-4 bg-background-blackpearl w-full pt-4 pb-6">
              <button
                type="button"
                className="flex items-center gap-1 text-persimmon text-base font-medium leading-[20px] pl-1 py-2 rounded-full"
                onClick={() => setIsDeleteTaskOpen(true)}
              >
                <Icons type="trash" />
                DELETE TASK
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-persimmon text-white rounded-full"
              >
                SAVE CHANGES
              </button>
            </div>
          </form>
        </div>
      </div>

      <TaskDelete
        isOpen={isDeleteTaskOpen}
        selectedTaskId={task.id}
        setIsTaskModifyCancel={onClose}
        onClose={() => setIsDeleteTaskOpen(false)}
      />

      <WarningDialog
        isOpen={isCancelWarningOpen}
        onClose={() => setIsCancelWarningOpen(false)}
        onProceed={onClose}
      />
    </>
  );
};

TaskModifyForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TaskModifyForm;
